<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-chart-line</v-icon>
                My Daily Attendance Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="fetchSummary">
                  <v-row class="py-3 px-3" align="end" dense>
                    <v-col cols="12" md="3">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="filters.from_date"
                        label="From Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        clearable
                        :max="filters.to_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="filters.to_date"
                        label="To Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        hide-details="auto"
                        clearable
                        :min="filters.from_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn
                        type="submit"
                        color="primary"
                        variant="flat"
                        :loading="loadingSummary"
                        prepend-icon="mdi-magnify"
                        density="compact"
                        height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12">
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                expand-icon="mdi-chevron-down"
                >
                <v-icon class="me-2">mdi-table</v-icon>
                Summary Results
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="summaryData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToExcel"
                          :disabled="!summaryData.length"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          :disabled="!summaryData.length"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-alert
                  v-if="!loadingSummary && filteredSummaryData.length === 0"
                  type="info"
                  variant="tonal"
                >
                  No summary data found for the selected criteria.
                </v-alert>

                <v-progress-linear
                  v-if="loadingSummary"
                  indeterminate
                  color="primary"
                  class="mb-4"
                />

                <div v-if="filteredSummaryData.length" class="table-responsive">
                  <v-table density="comfortable" class="table-bordered">
                    <thead>
                      <tr>
                        <th># SL</th>
                        <th>Date</th>
                        <th>ID No.</th>
                        <th>Employee</th>
                        <th>Designation</th>
                        <th>Department</th>
                        <th>Shift In</th>
                        <th>Shift Out</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                        <th>Punch In</th>
                        <th>Punch Out</th>
                        <th>Actual Status</th>
                        <th>Status</th>
                        <th>Late (min)</th>
                        <th>Early (min)</th>
                        <th>OT (hrs)</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in filteredSummaryData" :key="`${row.date}-${row.employee_id}`">
                        <td>{{ index + 1 }}</td>
                        <td>{{ formatDateView(row.date) }}</td>
                        <td>{{ row.employee_code }}</td>
                        <td>{{ row.employee_name }}</td>
                        <td>{{ row.designation }}</td>
                        <td>{{ row.department }}</td>
                        <td class="text-center">{{ row.shift?.in_time || row.in_time || '-' }}</td>
                        <td class="text-center">{{ row.shift?.out_time || row.out_time || '-' }}</td>
                        <td class="text-center">{{ row.check_in || '-' }}</td>
                        <td class="text-center">{{ row.check_out || '-' }}</td>
                        <td class="text-center">{{ row.punch_in || '-' }}</td>
                        <td class="text-center">{{ row.punch_out || '-' }}</td>
                        <td class="text-left">
                            <v-chip
                              :color="row.actual_status == 'Present' ? 'green' : 'red'"
                              size="small"
                            >
                              {{ row.actual_status || '-' }}
                            </v-chip>
                          </td>
                        <td class="text-left">
                          <v-chip
                            :color="getStatusColor(row.status?.id)"
                            :text-color="getStatusTextColor(row.status?.id)"
                            size="small"
                          >
                            {{ row.status?.name || '-' }}
                          </v-chip>
                        </td>
                        <td class="text-center">{{ formatNumber(row.late_minutes) }}</td>
                        <td class="text-center">{{ formatNumber(row.early_leaving_minutes) }}</td>
                        <td class="text-center">{{ formatNumber(row.overtime_minutes / 60, 2) }}</td>
                        <td class="text-left">{{ row.message }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

const loadingSummary = ref(false)
const summaryData = ref([])
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

const filters = reactive({
  from_date: '',
  to_date: '',
})

const formatNumber = (value, fractionDigits = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
}

const getStatusColor = (statusId) => {
  const colors = {
    1: 'green',      // Present
    2: 'red',        // Absent
    3: 'orange',     // Late
    4: 'blue',       // On Leave
    5: 'purple'      // Weekend/Holiday
  }
  return colors[statusId] || 'grey'
}

const getStatusTextColor = (statusId) => {
  return 'blue'
}

// Filtered summary data based on search
const filteredSummaryData = computed(() => {
  if (!search.value) {
    return summaryData.value
  }
  
  const searchLower = search.value.toLowerCase()
  return summaryData.value.filter(row => {
    const date = formatDateView(row.date).toLowerCase()
    const empName = (row.employee_name || '').toLowerCase()
    const empCode = (row.employee_code || '').toLowerCase()
    return date.includes(searchLower) || empName.includes(searchLower) || empCode.includes(searchLower)
  })
})

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})



const setDefaultDates = () => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  filters.from_date = startOfMonth;
  filters.to_date = today;
}

const fetchSummary = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.warning('Please select both From Date and To Date')
    return
  }

  loadingSummary.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
    }

    const response = await store.dispatch('getMyDailyAttendanceReport', params)
    if (response.success) {
      summaryData.value = response.data || []
    } else {
      summaryData.value = []
      toast.error(response.message || 'Failed to fetch summary report')
    }
  } catch (error) {
    summaryData.value = []
    toast.error(error.response?.data?.message || error.message || 'Failed to fetch summary report')
  } finally {
    loadingSummary.value = false
  }
}

// Export to Excel
const exportToExcel = () => {
  if (!summaryData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['My Daily Attendance Report'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`],
    [],
    ['# SL', 'Date', 'Employee', 'Shift In', 'Shift Out', 'Check In', 'Check Out', 'Punch In', 'Punch Out', 'Actual Status','Status', 'Late (min)', 'Early (min)', 'OT (hrs)'],
    []
  ]

  summaryData.value.forEach((row, index) => {
    data.push([
      index + 1,
      formatDateView(row.date),
      `${row.employee_name} (${row.employee_code})`,
      row.shift?.in_time || row.in_time || '-',
      row.shift?.out_time || row.out_time || '-',
      row.check_in || '-',
      row.check_out || '-',
      row.punch_in || '-',
      row.punch_out || '-',
      row.actual_status || '-',
      row.status?.name || '-',
      formatNumber(row.late_minutes),
      formatNumber(row.early_leaving_minutes),
      formatNumber(row.overtime_minutes / 60, 2)
    ])
  })

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'attendance_report_' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!summaryData.value.length) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Daily Attendance Report</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 11px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: landscape; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>Daily Attendance Report</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}</p>
              <p><strong>Total Records:</strong> ${summaryData.value.length}</p>
            </div>
          </div>
          <table class="table mt-2">
            <thead>
              <tr>
                <th>SN</th>
                <th>Date</th>
                <th>Employee</th>
                <th class="text-center">Shift In</th>
                <th class="text-center">Shift Out</th>
                <th class="text-center">Check In</th>
                <th class="text-center">Check Out</th>
                <th class="text-center">Punch In</th>
                <th class="text-center">Punch Out</th>
                <th class="text-center">Actual Status</th>
                <th class="text-center">Status</th>
                <th class="text-center">Late</th>
                <th class="text-center">Early</th>
                <th class="text-center">OT</th>
              </tr>
            </thead>
            <tbody>
              ${summaryData.value.map((row, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${formatDateView(row.date)}</td>
                  <td>${row.employee_name} (${row.employee_code})</td>
                  <td class="text-center">${row.shift?.in_time || row.in_time || '-'}</td>
                  <td class="text-center">${row.shift?.out_time || row.out_time || '-'}</td>
                  <td class="text-center">${row.check_in || '-'}</td>
                  <td class="text-center">${row.check_out || '-'}</td>
                  <td class="text-center">${row.punch_in || '-'}</td>
                  <td class="text-center">${row.punch_out || '-'}</td>
                  <td class="text-center">${row.actual_status || '-'}</td>
                  <td class="text-center">${row.status?.name || '-'}</td>
                  <td class="text-center">${formatNumber(row.late_minutes)}</td>
                  <td class="text-center">${formatNumber(row.early_leaving_minutes)}</td>
                  <td class="text-center">${formatNumber(row.overtime_minutes / 60, 2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

onMounted(async () => {
  setDefaultDates()
})
</script>

<style scoped>
.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.panel-header {
  font-weight: 600;
}

.table-responsive {
  overflow-x: auto;
}

.text-muted {
  color: #999;
}

.font-weight-bold {
  font-weight: 600;
}
</style>